import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link, Navigate } from 'react-router-dom';
import * as authService from '../services/auth.service';
import { useAuth } from '../context/AuthContext';

const ResetPasswordPage: React.FC = () => {
  const { resetToken } = useParams<{ resetToken: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [validToken, setValidToken] = useState(true);
  
  // Minimum password requirements
  const MIN_PASSWORD_LENGTH = 6;

  // Redirect to dashboard if already logged in
  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate input
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (!resetToken) {
      setError('Invalid reset token');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      // Call API to reset password
      await authService.resetPassword(resetToken, password);
      
      // Show success message
      setSuccess('Your password has been reset successfully');
      
      // Clear form
      setPassword('');
      setConfirmPassword('');
      
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'Failed to reset password. The token may be invalid or expired.'
      );
      setValidToken(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background dark:bg-dark-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-card dark:bg-dark-card p-8 rounded-lg shadow-lg">
        <div>
          <Link to="/" className="flex justify-center">
            <h1 className="text-2xl font-bold text-primary dark:text-white">ProCreative</h1>
          </Link>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground dark:text-dark-foreground">
            Reset Password
          </h2>
        </div>
        
        {error && (
          <div className="bg-destructive/10 dark:bg-dark-destructive/20 text-destructive dark:text-dark-destructive p-3 rounded-md">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-success/10 dark:bg-dark-success/20 text-success dark:text-white font-medium p-3 rounded-md">
            {success}
            <p className="mt-2 text-sm">Redirecting to login page...</p>
          </div>
        )}
        
        {validToken ? (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md space-y-4">
              <div>
                <label htmlFor="password" className="block mb-1 text-sm font-medium text-foreground dark:text-dark-foreground">
                  New Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-input dark:border-dark-input rounded-md focus:outline-none focus:ring-primary dark:focus:ring-dark-primary focus:border-primary dark:focus:border-dark-primary sm:text-sm bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  placeholder="Enter new password"
                />
                <p className="mt-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                  Password must be at least {MIN_PASSWORD_LENGTH} characters
                </p>
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block mb-1 text-sm font-medium text-foreground dark:text-dark-foreground">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-input dark:border-dark-input rounded-md focus:outline-none focus:ring-primary dark:focus:ring-dark-primary focus:border-primary dark:focus:border-dark-primary sm:text-sm bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent rounded-md text-white bg-primary hover:bg-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-dark-primary disabled:opacity-50"
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-6 text-center">
            <p className="text-foreground dark:text-dark-foreground mb-4">
              The reset link appears to be invalid or has expired.
            </p>
            <Link 
              to="/login" 
              className="inline-block px-4 py-2 text-white bg-primary hover:bg-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90 rounded-md"
            >
              Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage; 